import { createSelector } from 'reselect';
import { roundNumber } from 'utils/general';
import {
  getLatestDualSector,
  getLatestSingleSector,
  isSectorsSame
} from 'utils/countryProfile';
import { getScoreData } from 'utils/data';
import { SECTORS } from 'constants/common';

const getData = (state) => state.countryProfile.data;
const getcountries = (state) => state.countryProfile.mapCountries;
const getCountry = (state) => state.countryProfile.country;
const getFilters = (state) => state.countryProfile.filters;
const getDataReport = (state) => state.countryProfile.dataReport;

export const getCountryData = createSelector(
  [getData, getCountry],
  (data, country) => data[country] || false
);

const getOverview = (state) => state.overview;
export const parseOverviewData = createSelector(
  [getOverview],
  (overviewData) => {
    if (!overviewData.length) return { overview: [], legend: [] };

    const overview = overviewData.map((item) => ({
      name: item.name,
      avg: item.avg,
      values: [{ value: Math.floor(Math.random() * (100 - 20 + 1)) + 20 }] // emulate fake data
    }));
    const legend = [{ country: 1, value: 'Nigeria' }]; // emulate fake data
    legend.push({ value: 'Average score' });
    return { overview, legend };
  }
);

export const getCountryDataReport = createSelector([getFilters, getDataReport], (filters, dataReport) => {
  const { years: selectedYears = [] } = filters || {};
  const selectedYear = selectedYears.length ? String(selectedYears[0]) : '2017';
  if (Array.isArray(dataReport)) {
    return dataReport.find(el => String(el.year) === selectedYear) || {};
  }
  return dataReport;
});

export const parseMapCountries = createSelector([getcountries], (countries) => {
  if (!countries.length) return {};
  const mapCountries = {};
  let yearsOfDataArray = [];

  countries.forEach((item) => {
    if (!item['country-sectors'].length) return;

    let fillKey = 'defaultFill';
    let score;
    let rank;
    let sector;
    let yearOfData = null;
    let allYears = [];
    const { name, iso } = item;
    const hasDualSector =
      item['country-sectors'].length && item['country-sectors'].length > 1;
    const { mining, oilGas, selectedYear, allYears: allYearsDual } = getLatestDualSector(item);
    if (hasDualSector && mining && oilGas) {
      yearOfData = selectedYear;
      allYears = allYearsDual;
      const miningScore = roundNumber(mining.score);
      const oilGasScore = roundNumber(oilGas.score);
      // although we have score data already in muining, oilGas but getScore data will
      // filter out only scores from rest of the obj
      score = {
        mining: getScoreData(mining),
        'oil-gas': getScoreData(oilGas)
      };
      rank = {
        mining: mining.rank,
        'oil-gas': oilGas.rank
      };

      switch (true) {
        // failing scores
        case miningScore < 30 && oilGasScore < 30:
          fillKey = 'diagonal-equal-failing';
          break;
        case miningScore < 30 && oilGasScore < 45:
          fillKey = 'diagonal-failing-weak';
          break;
        case miningScore < 30 && oilGasScore < 60:
          fillKey = 'diagonal-failing-partial';
          break;
        case miningScore < 30 && oilGasScore < 75:
          fillKey = 'diagonal-failing-satisfactory';
          break;
        case miningScore < 30 && oilGasScore <= 100:
          fillKey = 'diagonal-failing-good';
          break;
        // weak scores
        case miningScore >= 30 && miningScore < 45 && oilGasScore < 30:
          fillKey = 'diagonal-weak-failing';
          break;
        case miningScore < 45 && oilGasScore < 45:
          fillKey = 'diagonal-equal-weak';
          break;
        case miningScore >= 30 && miningScore < 45 && oilGasScore < 60:
          fillKey = 'diagonal-weak-partial';
          break;
        case miningScore >= 30 && miningScore < 45 && oilGasScore < 75:
          fillKey = 'diagonal-weak-good';
          break;
        case miningScore >= 30 && miningScore < 45 && oilGasScore >= 75:
          fillKey = 'diagonal-weak-satisfactory';
          break;
        // partial scores
        case miningScore >= 45 && miningScore < 60 && oilGasScore < 30:
          fillKey = 'diagonal-partial-failing';
          break;
        case miningScore >= 45 && miningScore < 60 && oilGasScore < 45:
          fillKey = 'diagonal-partial-weak';
          break;
        case miningScore < 60 && oilGasScore < 60:
          fillKey = 'diagonal-equal-partial';
          break;
        case miningScore >= 45 &&
          miningScore < 60 &&
          oilGasScore >= 60 &&
          oilGasScore < 75:
          fillKey = 'diagonal-partial-satisfactory';
          break;
        case miningScore >= 45 && miningScore < 60 && oilGasScore >= 75:
          fillKey = 'diagonal-partial-good';
          break;
        // satisfactory scores
        case miningScore >= 60 && miningScore < 75 && oilGasScore < 30:
          fillKey = 'diagonal-satisfactory-failing';
          break;
        case miningScore >= 60 && miningScore < 75 && oilGasScore < 45:
          fillKey = 'diagonal-satisfactory-weak';
          break;
        case miningScore >= 60 && miningScore < 75 && oilGasScore < 60:
          fillKey = 'diagonal-satisfactory-partial';
          break;
        case miningScore >= 60 && miningScore < 75 && oilGasScore < 75:
          fillKey = 'diagonal-equal-satisfactory';
          break;
        case miningScore >= 60 && miningScore < 75 && oilGasScore >= 75:
          fillKey = 'diagonal-satisfactory-good';
          break;
        // good scores
        case miningScore >= 75 && oilGasScore < 30:
          fillKey = 'diagonal-good-failing';
          break;
        case miningScore >= 75 && oilGasScore < 45:
          fillKey = 'diagonal-good-weak';
          break;
        case miningScore >= 75 && oilGasScore < 60:
          fillKey = 'diagonal-good-partial';
          break;
        case miningScore >= 75 && oilGasScore < 75:
          fillKey = 'diagonal-good-satisfactory';
          break;
        case miningScore >= 75 && oilGasScore >= 75:
          fillKey = 'diagonal-equal-good';
          break;
        // default
        default:
          fillKey;
      }
    } else {
      let sectorId = undefined;
      let singleSectorLatestScore = null;
      let sectorName = null;
      if (item['country-sectors'].length) {
        const { selectedSector, selectedYear: selectedYearSingle, allYears: allYearsSingle } =
          getLatestSingleSector(item);
        allYears = allYearsSingle;
        // although we have score data already in selectedsector
        // but this will filters out score data only from the rest of object
        score = getScoreData(selectedSector);
        rank = selectedSector.rank;
        sectorId = selectedSector.sector.id;
        sectorName = selectedSector.sector.name;
        yearOfData = selectedYearSingle;
        singleSectorLatestScore = score[`score${yearOfData}`];
      } else {
        score = undefined;
        rank = undefined;
      }
      sector = sectorId
        ? (SECTORS.find(sec => sec.id === +sectorId || isSectorsSame(sec.label, sectorName)) || {}).label
        : undefined;

      switch (true) {
        case singleSectorLatestScore < 30:
          fillKey = 'failing';
          break;
        case singleSectorLatestScore < 45:
          fillKey = 'weak';
          break;
        case singleSectorLatestScore < 60:
          fillKey = 'partial';
          break;
        case singleSectorLatestScore < 75:
          fillKey = 'satisfactory';
          break;
        case singleSectorLatestScore <= 100:
          fillKey = 'good';
          break;
        default:
          fillKey;
      }
    }
    if (!yearsOfDataArray.includes(yearOfData)) {
      yearsOfDataArray.push(yearOfData);
    }
    const classSuffix = '';
    mapCountries[iso] = {
      name,
      iso,
      rank,
      score,
      sector: sector || null,
      fillKey,
      yearOfData,
      allYears,
      classSuffix
    };
  });
// On top we calculated the latest year of data for each item, here we are
// getting global latest year of data among all countries so we can categorize the rest of the years
// with previous assessment
  if (yearsOfDataArray.length) {
    yearsOfDataArray = yearsOfDataArray.sort((a, b) => b - a);
    const latestYear = yearsOfDataArray[0];
    Object.keys(mapCountries).forEach((iso) => {
      // eslint-disable-next-line prefer-const
      let { yearOfData, classSuffix, fillKey } = mapCountries[iso];
      if (
        yearsOfDataArray.length > 1 &&
        String(latestYear) !== String(yearOfData)
      ) {
        classSuffix = '-v1';
        fillKey = 'failing-v1'; // dummy assignment
      }
      mapCountries[iso] = {
        ...mapCountries[iso],
        latestYear,
        classSuffix,
        fillKey
      };
    });
  }

  return mapCountries;
});



// WEBPACK FOOTER //
// ./app/selectors/country-profiles.js